<template>
  <v-container fluid class="pa-6 alerts-followup">
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card elevation="0" outlined class="monitor-header-card">
          <v-card-text class="monitor-header py-4 px-6 d-flex align-center">
            <v-avatar color="#ef233c" size="54" class="mr-4 monitor-header__avatar" rounded>
              <v-icon dark>mdi-alert-decagram-outline</v-icon>
            </v-avatar>
            <div class="flex-grow-1 monitor-header__copy">
              <div class="monitor-header__text">
                <div class="monitor-header__kicker">
                  <span class="monitor-header__kicker-line"></span>
                  <span>{{ $t('Reports.AlertTracking.header.kicker') }}</span>
                </div>
                <div class="monitor-header__title mb-0">
                  <span class="monitor-header__title-main">{{ $t('Reports.AlertTracking.header.titleMain') }}</span>
                  <span class="monitor-header__title-ghost">{{ $t('Reports.AlertTracking.header.titleGhost') }}</span>
                </div>
                <div class="monitor-header__title-accent"></div>
              </div>
            </div>
            <v-tooltip bottom color="blue-grey darken-4">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" class="str-language-control" v-on="on">
                  <v-icon small color="primary" class="mr-2">mdi-translate</v-icon>
                  <v-btn-toggle :value="currentLocale" mandatory dense class="str-language-toggle" @change="setLocale">
                    <v-btn small value="es" class="str-language-btn"
                      :class="{ 'str-language-btn--active': currentLocale === 'es' }"
                      :aria-label="$t('Reports.AlertTracking.language.spanish')">
                      ES
                    </v-btn>
                    <v-btn small value="en" class="str-language-btn"
                      :class="{ 'str-language-btn--active': currentLocale === 'en' }"
                      :aria-label="$t('Reports.AlertTracking.language.english')">
                      EN
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </template>
              <span class="white--text">{{ $t('Reports.AlertTracking.language.tooltip') }}</span>
            </v-tooltip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div class="signal-grid mb-5">
      <button
        v-for="card in summaryCards"
        :key="card.key"
        type="button"
        class="signal-tile"
        :class="'signal-tile--' + card.tone"
        @click="quickFilter(card.filter)"
      >
        <span class="signal-tile__main">
          <span class="signal-tile__icon"><v-icon>{{ card.icon }}</v-icon></span>
          <span class="signal-tile__info">
            <span class="signal-tile__value">{{ card.value }}</span>
            <span class="signal-tile__label">{{ card.label }}</span>
          </span>
        </span>
        <span class="signal-tile__foot">
          <span class="signal-tile__accent"></span>
          <span class="signal-tile__cta">
            {{ card.link }}
            <v-icon size="13">mdi-arrow-right</v-icon>
          </span>
        </span>
      </button>
    </div>

    <section class="alerts-panel">
      <div class="alerts-toolbar">
        <v-text-field
          v-model="search"
          dense
          outlined
          hide-details
          append-icon="mdi-magnify"
          :label="$t('Reports.AlertTracking.toolbar.searchPlaceholder')"
          class="alerts-search"
        ></v-text-field>
        <div class="alerts-filter-chips">
          <v-chip
            v-for="chip in activeFilterChips"
            :key="chip.key"
            small
            close
            class="alerts-filter-chip"
            @click:close="removeFilter(chip.key)"
          >
            <v-icon small left>{{ chip.icon }}</v-icon>
            {{ chip.label }}: {{ chip.value }}
          </v-chip>
          <span v-if="activeFilterChips.length === 0" class="alerts-filter-empty">
            {{ $t('Reports.AlertTracking.toolbar.noFilters') }}
          </span>
        </div>
        <div class="alerts-toolbar__actions">
          <v-btn
            color="#0f5bff"
            dark
            depressed
            class="alerts-filter-btn"
            @click="filtersDialog = true"
          >
            <v-icon small left>mdi-tune-variant</v-icon>
            {{ $t('Reports.AlertTracking.toolbar.filters') }}
            <span v-if="activeFiltersCount" class="alerts-filter-btn__badge">{{ activeFiltersCount }}</span>
          </v-btn>
          <v-btn
            outlined
            color="#0f5bff"
            class="alerts-reset-btn"
            @click="resetFilters"
          >
            <v-icon small left>mdi-filter-remove-outline</v-icon>
            {{ $t('Reports.AlertTracking.toolbar.clear') }}
          </v-btn>
        </div>
        <div class="alerts-live">
          <span class="alerts-live__dot"></span>
          {{ $t('Reports.AlertTracking.toolbar.autoRefresh') }}
          <span class="alerts-live__count mono">{{ autoRefreshLabel }}</span>
          <v-btn
            icon
            x-small
            class="alerts-live__btn"
            :title="$t('Reports.AlertTracking.toolbar.refreshNow')"
            :loading="loading"
            @click="refreshNow"
          >
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <div class="alerts-table-wrap">
        <table class="signal-table">
          <thead>
            <tr>
              <th>{{ $t('Reports.AlertTracking.headers.prioridad') }}</th>
              <th>{{ $t('Reports.AlertTracking.headers.alerta') }}</th>
              <th>{{ $t('Reports.AlertTracking.headers.msv') }}</th>
              <th>{{ $t('Reports.AlertTracking.headers.valor') }}</th>
              <th>{{ $t('Reports.AlertTracking.headers.inicio') }}</th>
              <th>{{ $t('Reports.AlertTracking.headers.ultimaDeteccion') }}</th>
              <th>{{ $t('Reports.AlertTracking.headers.estado') }}</th>
              <th>{{ $t('Reports.AlertTracking.headers.transcurrido') }}</th>
              <th class="signal-table__ah">{{ $t('Reports.AlertTracking.headers.acciones') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="alert in paginatedAlerts"
              :key="alert.id"
              class="signal-row"
              :class="'signal-row--' + alert.levelTone"
            >
              <td :data-label="$t('Reports.AlertTracking.headers.prioridad')">
                <span class="sev-pill" :class="'sev-pill--' + alert.levelTone">
                  <v-icon size="15">{{ levelIcon(alert.levelTone) }}</v-icon>
                  {{ alert.level }}
                </span>
              </td>
              <td :data-label="$t('Reports.AlertTracking.headers.alerta')">
                <div class="cell-strong mono">{{ alert.code }}</div>
                <div class="cell-sub">{{ alert.message }}</div>
              </td>
              <td :data-label="$t('Reports.AlertTracking.headers.msv')">
                <div class="cell-strong">{{ alert.machine }}</div>
                <div class="cell-sub"><v-icon size="13">mdi-access-point</v-icon>{{ alert.sensor }}</div>
                <div class="cell-sub"><v-icon size="13">mdi-chart-bell-curve</v-icon>{{ alert.variable }}</div>
              </td>
              <td :data-label="$t('Reports.AlertTracking.headers.valor')">
                <span class="readout mono" :class="'readout--' + alert.levelTone">{{ alert.value }}</span>
              </td>
              <td :data-label="$t('Reports.AlertTracking.headers.inicio')">
                <div class="mono">{{ alert.startDate }}</div>
                <div class="cell-sub mono">{{ alert.startTime }}</div>
              </td>
              <td :data-label="$t('Reports.AlertTracking.headers.ultimaDeteccion')">
                <div class="mono">{{ alert.lastDate }}</div>
                <div class="cell-sub mono">{{ alert.lastTime }}</div>
              </td>
              <td :data-label="$t('Reports.AlertTracking.headers.estado')">
                <span class="state-chip" :class="'state-chip--' + statusTone(alert.status)">
                  <span class="state-chip__dot"></span>
                  {{ statusLabel(alert.status) }}
                </span>
              </td>
              <td :data-label="$t('Reports.AlertTracking.headers.transcurrido')">
                <span class="elapsed mono">
                  <v-icon size="14">mdi-timer-sand</v-icon>{{ alert.elapsed }}
                </span>
              </td>
              <td :data-label="$t('Reports.AlertTracking.headers.acciones')" class="signal-table__actions">
                <div class="alerts-actions">
                  <v-tooltip top color="#0f2440" content-class="alerts-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small class="alerts-action-btn" v-bind="attrs" v-on="on" @click="openDetails(alert)">
                        <v-icon small>mdi-eye-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('Reports.AlertTracking.table.viewDetail') }}</span>
                  </v-tooltip>
                  <v-tooltip top color="#0f2440" content-class="alerts-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-btn
                          icon
                          small
                          class="alerts-action-btn"
                          :disabled="alert.status === 'CERRADA'"
                          @click="openChangeStatus(alert)"
                        >
                          <v-icon small>mdi-check</v-icon>
                        </v-btn>
                      </span>
                    </template>
                    <span>{{ alert.status === 'CERRADA' ? $t('Reports.AlertTracking.table.alertClosed') : $t('Reports.AlertTracking.table.changeStatus') }}</span>
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="paginatedAlerts.length === 0" class="alerts-empty">
          <v-icon size="34">mdi-radar</v-icon>
          {{ $t('Reports.AlertTracking.table.empty') }}
        </div>
      </div>

      <footer class="alerts-footer">
        <span>{{ $t('Reports.AlertTracking.footer.showing', { start: paginationStart, end: paginationEnd, total: filteredAlerts.length }) }}</span>
        <v-pagination v-model="page" :length="pageCount" total-visible="6" color="#0f5bff"></v-pagination>
        <v-select
          v-model="itemsPerPage"
          :items="[7, 10, 15]"
          dense
          outlined
          hide-details
          :suffix="$t('Reports.AlertTracking.footer.perPage')"
          class="alerts-page-size"
        ></v-select>
      </footer>
    </section>

    <v-dialog v-model="filtersDialog" max-width="880">
      <div class="filters-dialog">
        <button class="filters-dialog__close" @click="filtersDialog = false">
          <v-icon>mdi-close</v-icon>
        </button>
        <div class="filters-dialog__header">
          <div class="filters-dialog__icon">
            <v-icon>mdi-tune-variant</v-icon>
          </div>
          <div>
            <span>{{ $t('Reports.AlertTracking.filtersDialog.eyebrow') }}</span>
            <h2>{{ $t('Reports.AlertTracking.filtersDialog.title') }}</h2>
          </div>
        </div>

        <div class="filters-dialog__grid">
          <v-text-field
            v-model="search"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-magnify"
            :label="$t('Reports.AlertTracking.filtersDialog.searchText')"
            class="filters-dialog__wide"
          ></v-text-field>
          <v-select
            v-model="filters.machine"
            :items="machineOptions"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-factory"
            :label="$t('Reports.AlertTracking.filtersDialog.maquina')"
          ></v-select>
          <v-select
            v-model="filters.sensor"
            :items="sensorOptions"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-access-point"
            :label="$t('Reports.AlertTracking.filtersDialog.sensor')"
          ></v-select>
          <v-select
            v-model="filters.variable"
            :items="variableOptions"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-chart-bell-curve"
            :label="$t('Reports.AlertTracking.filtersDialog.variable')"
          ></v-select>
          <v-select
            v-model="filters.level"
            :items="levelOptions"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-alert-octagon-outline"
            :label="$t('Reports.AlertTracking.filtersDialog.nivel')"
          ></v-select>
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-list-status"
            :label="$t('Reports.AlertTracking.filtersDialog.estado')"
          ></v-select>
          <v-text-field
            v-model="filters.startDate"
            dense
            outlined
            hide-details
            type="date"
            prepend-inner-icon="mdi-calendar-start"
            :label="$t('Reports.AlertTracking.filtersDialog.desde')"
            :max="filters.endDate || undefined"
          ></v-text-field>
          <v-text-field
            v-model="filters.endDate"
            dense
            outlined
            hide-details
            type="date"
            prepend-inner-icon="mdi-calendar-end"
            :label="$t('Reports.AlertTracking.filtersDialog.hasta')"
            :min="filters.startDate || undefined"
          ></v-text-field>
        </div>

        <div class="filters-dialog__actions">
          <v-btn outlined color="#0f5bff" class="alerts-reset-btn" @click="resetFilters">
            <v-icon small left>mdi-filter-remove-outline</v-icon>
            {{ $t('Reports.AlertTracking.filtersDialog.clearFilters') }}
          </v-btn>
          <v-btn color="#0f5bff" dark depressed class="alerts-filter-btn" @click="filtersDialog = false">
            <v-icon small left>mdi-check</v-icon>
            {{ $t('Reports.AlertTracking.filtersDialog.apply') }}
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <v-dialog v-model="statusDialog" max-width="620">
      <div v-if="selectedAlert" class="change-dialog">
        <button class="change-dialog__close" @click="statusDialog = false">
          <v-icon>mdi-close</v-icon>
        </button>
        <div class="change-dialog__header">
          <div class="change-dialog__icon" :class="'change-dialog__icon--' + selectedAlert.levelTone">
            <v-icon>mdi-alert</v-icon>
          </div>
          <div>
            <h2>{{ $t('Reports.AlertTracking.statusDialog.title') }}</h2>
            <p>{{ selectedAlert.code }} - {{ selectedAlert.message }}</p>
          </div>
        </div>

        <div class="change-dialog__summary">
          <div>
            <span>{{ $t('Reports.AlertTracking.statusDialog.maquina') }}</span>
            <strong>{{ selectedAlert.machine }}</strong>
            <small>{{ selectedAlert.sensor }}</small>
          </div>
          <div>
            <span>{{ $t('Reports.AlertTracking.statusDialog.detectedValue') }}</span>
            <strong :class="'alert-value--' + selectedAlert.levelTone">{{ selectedAlert.value }}</strong>
          </div>
          <div>
            <span>{{ $t('Reports.AlertTracking.statusDialog.currentStatus') }}</span>
            <strong>{{ statusLabel(selectedAlert.status) }}</strong>
          </div>
        </div>

        <div class="change-dialog__section-title">{{ $t('Reports.AlertTracking.statusDialog.newStatus') }}</div>
        <div class="state-grid">
          <button
            v-for="state in availableStateActions"
            :key="state.value"
            class="state-card"
            :class="[
              'state-card--' + state.tone,
              { 'state-card--active': newStatus === state.value }
            ]"
            @click="newStatus = state.value"
          >
            <v-icon>{{ state.icon }}</v-icon>
            <span>{{ state.label }}</span>
          </button>
        </div>

        <v-textarea
          v-model="comment"
          outlined
          counter="500"
          rows="4"
          :label="$t('Reports.AlertTracking.statusDialog.commentLabel')"
          :placeholder="$t('Reports.AlertTracking.statusDialog.commentPlaceholder')"
          class="mt-4"
        ></v-textarea>

        <div class="change-dialog__actions">
          <v-btn outlined class="change-dialog__cancel" @click="statusDialog = false">{{ $t('Reports.AlertTracking.statusDialog.cancel') }}</v-btn>
          <v-btn
            color="#0f5bff"
            dark
            depressed
            class="change-dialog__confirm"
            :disabled="!canConfirmStatus"
            @click="confirmStatus"
          >
            {{ $t('Reports.AlertTracking.statusDialog.confirm') }}
          </v-btn>
        </div>

        <div class="change-dialog__note">
          <v-icon small>mdi-information-outline</v-icon>
          {{ $t('Reports.AlertTracking.statusDialog.note') }}
        </div>
      </div>
    </v-dialog>

    <v-dialog v-model="closeConfirmDialog" max-width="520">
      <div class="close-confirm-dialog">
        <button class="close-confirm-dialog__close" @click="closeConfirmDialog = false">
          <v-icon>mdi-close</v-icon>
        </button>
        <div class="close-confirm-dialog__header">
          <div class="close-confirm-dialog__icon">
            <v-icon>mdi-shield-alert-outline</v-icon>
          </div>
          <div>
            <span>{{ $t('Reports.AlertTracking.closeDialog.eyebrow') }}</span>
            <h2>{{ $t('Reports.AlertTracking.closeDialog.title') }}</h2>
            <p v-if="selectedAlert">
              {{ selectedAlert.code }} - {{ selectedAlert.message }}
            </p>
          </div>
        </div>
        <div class="close-confirm-dialog__body">
          {{ $t('Reports.AlertTracking.closeDialog.body') }}
        </div>
        <div class="close-confirm-dialog__actions">
          <v-btn outlined class="change-dialog__cancel" @click="closeConfirmDialog = false">
            {{ $t('Reports.AlertTracking.closeDialog.cancel') }}
          </v-btn>
          <v-btn
            color="#0f5bff"
            dark
            depressed
            class="change-dialog__confirm"
            @click="confirmCloseStatus"
          >
            {{ $t('Reports.AlertTracking.closeDialog.confirm') }}
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <v-dialog v-model="detailsDialog" max-width="780" content-class="details-dialog-shell">
      <div v-if="selectedAlert" class="details-dialog">
        <div class="details-dialog__hero" :class="'details-dialog__hero--' + (selectedAlert.levelTone || 'normal')">
          <div class="details-dialog__hero-icon">
            <v-icon>mdi-alert-decagram-outline</v-icon>
          </div>
          <div class="details-dialog__hero-copy">
            <span class="details-dialog__eyebrow">{{ $t('Reports.AlertTracking.details.eyebrow') }}</span>
            <h2 class="details-dialog__code">{{ selectedAlert.code }}</h2>
            <p class="details-dialog__msg">{{ selectedAlert.message || $t('Reports.AlertTracking.details.noMessage') }}</p>
            <div class="details-dialog__badges">
              <span class="priority-pill" :class="'priority-pill--' + selectedAlert.levelTone">
                <v-icon small>mdi-alert</v-icon>
                {{ selectedAlert.level || '—' }}
              </span>
              <span class="status-pill" :class="'status-pill--' + statusTone(selectedAlert.status)">
                {{ statusLabel(selectedAlert.status) }}
              </span>
              <span class="details-dialog__type-chip">{{ selectedAlert.type || '—' }}</span>
            </div>
          </div>
          <v-btn icon class="details-dialog__close" @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="details-dialog__body">
          <div class="details-dialog__section-label">
            <v-icon small>mdi-information-outline</v-icon>
            {{ $t('Reports.AlertTracking.details.sectionGeneral') }}
          </div>
          <div class="details-dialog__grid">
            <div>
              <small>{{ $t('Reports.AlertTracking.details.maquina') }}</small>
              <strong>{{ selectedAlert.machine || '—' }}</strong>
            </div>
            <div>
              <small>{{ $t('Reports.AlertTracking.details.sensor') }}</small>
              <strong>{{ selectedAlert.sensor || '—' }}</strong>
            </div>
            <div>
              <small>{{ $t('Reports.AlertTracking.details.variable') }}</small>
              <strong>{{ selectedAlert.variable || '—' }}</strong>
            </div>
            <div>
              <small>{{ $t('Reports.AlertTracking.details.tipoFalla') }}</small>
              <strong>{{ selectedAlert.tipoFalla || 'N/A' }}</strong>
            </div>
            <div>
              <small>{{ $t('Reports.AlertTracking.details.detectedValue') }}</small>
              <strong class="details-dialog__value" :class="'alert-value--' + selectedAlert.levelTone">{{ selectedAlert.value || 'N/A' }}</strong>
            </div>
            <div>
              <small>{{ $t('Reports.AlertTracking.details.totalDetections') }}</small>
              <strong>{{ selectedAlert.detections }}</strong>
            </div>
            <div>
              <small>{{ $t('Reports.AlertTracking.details.startDate') }}</small>
              <strong>{{ selectedAlert.fechaInicioFull || '—' }}</strong>
            </div>
            <div>
              <small>{{ $t('Reports.AlertTracking.details.lastDetection') }}</small>
              <strong>{{ selectedAlert.ultimaDeteccionFull || '—' }}</strong>
            </div>
            <div>
              <small>{{ $t('Reports.AlertTracking.details.elapsed') }}</small>
              <strong>{{ selectedAlert.tiempo || '—' }}</strong>
            </div>
          </div>

          <div class="details-dialog__section">
            <h3><v-icon small>mdi-radar</v-icon> {{ $t('Reports.AlertTracking.details.historyDetections') }}</h3>
            <div v-if="(selectedAlert.detecciones || []).length" class="details-table-wrap">
              <table class="details-table">
                <thead>
                  <tr>
                    <th>{{ $t('Reports.AlertTracking.details.detFecha') }}</th>
                    <th>{{ $t('Reports.AlertTracking.details.detValor') }}</th>
                    <th>{{ $t('Reports.AlertTracking.details.detNivel') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(det, i) in selectedAlert.detecciones" :key="'det' + i">
                    <td>{{ det.fecha }}</td>
                    <td class="details-table__num">{{ det.valor != null ? det.valor : 'N/A' }}</td>
                    <td>{{ det.nivel || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="details-empty">
              <v-icon small>mdi-radar</v-icon>
              {{ $t('Reports.AlertTracking.details.noDetections') }}
            </div>
          </div>

          <div class="details-dialog__section">
            <h3><v-icon small>mdi-bell-ring-outline</v-icon> {{ $t('Reports.AlertTracking.details.historyNotifications') }}</h3>
            <div v-if="(selectedAlert.notificaciones || []).length" class="details-table-wrap">
              <table class="details-table">
                <thead>
                  <tr>
                    <th>{{ $t('Reports.AlertTracking.details.notMedio') }}</th>
                    <th>{{ $t('Reports.AlertTracking.details.notEstado') }}</th>
                    <th>{{ $t('Reports.AlertTracking.details.notFecha') }}</th>
                    <th>{{ $t('Reports.AlertTracking.details.notIntentos') }}</th>
                    <th>{{ $t('Reports.AlertTracking.details.notError') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(noti, i) in selectedAlert.notificaciones" :key="'not' + i">
                    <td>{{ noti.medio || '—' }}</td>
                    <td>
                      <span class="envio-pill" :class="'envio-pill--' + (noti.estadoEnvio || '').toLowerCase()">
                        {{ noti.estadoEnvio || '—' }}
                      </span>
                    </td>
                    <td>{{ noti.fechaEnvio || $t('Reports.AlertTracking.details.notPending') }}</td>
                    <td class="details-table__num">{{ noti.intentos }}</td>
                    <td>{{ noti.mensajeError || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="details-empty">
              <v-icon small>mdi-bell-off-outline</v-icon>
              {{ $t('Reports.AlertTracking.details.noNotifications') }}
            </div>
          </div>

          <div class="details-dialog__section">
            <h3><v-icon small>mdi-timeline-text-outline</v-icon> {{ $t('Reports.AlertTracking.details.operativeTracking') }}</h3>
            <div v-if="(selectedAlert.history || []).length" class="details-dialog__timeline">
              <div v-for="(event, i) in selectedAlert.history" :key="'seg' + i" class="timeline-item">
                <span class="timeline-item__dot" :class="'timeline-item__dot--' + statusTone(event.action)"></span>
                <div class="timeline-item__body">
                  <div class="timeline-item__head">
                    <strong>{{ statusLabel(event.action) }}</strong>
                    <small>{{ event.date }}</small>
                  </div>
                  <span v-if="event.usuario" class="timeline-item__user">
                    <v-icon x-small>mdi-account-outline</v-icon>
                    {{ event.usuario }}
                  </span>
                  <p>{{ event.comment || $t('Reports.AlertTracking.details.noComment') }}</p>
                </div>
              </div>
            </div>
            <div v-else class="details-empty">
              <v-icon small>mdi-timeline-remove-outline</v-icon>
              {{ $t('Reports.AlertTracking.details.noTracking') }}
            </div>
          </div>
        </div>
      </div>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" color="#0f2440" timeout="2600">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "SeguimientoAlertas",
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: "",
      page: 1,
      itemsPerPage: 7,
      autoRefreshSeconds: 180,
      autoRefreshTimer: null,
      statusDialog: false,
      detailsDialog: false,
      filtersDialog: false,
      closeConfirmDialog: false,
      selectedAlert: null,
      newStatus: "RECONOCIDA",
      comment: "",
      loading: false,
      saving: false,
      snackbar: {
        show: false,
        text: "",
      },
      filters: {
        machine: "__ALL__",
        sensor: "__ALL__",
        variable: "__ALL__",
        level: "__ALL__",
        status: "__ALL__",
        startDate: null,
        endDate: null,
      },
      alerts: [],
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },
    stateActions() {
      return [
        { value: "EN_ATENCION", label: this.$t("Reports.AlertTracking.stateActions.enAtencion"), icon: "mdi-wrench-outline", tone: "blue" },
        { value: "CERRADA", label: this.$t("Reports.AlertTracking.stateActions.cerrarAlerta"), icon: "mdi-shield-check-outline", tone: "green" },
      ];
    },
    autoRefreshLabel() {
      const total = Math.max(0, this.autoRefreshSeconds);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${String(s).padStart(2, "0")}`;
    },
    machineOptions() {
      return this.uniqueOptions("machine");
    },
    sensorOptions() {
      return this.uniqueOptions("sensor");
    },
    variableOptions() {
      return this.uniqueOptions("variable");
    },
    levelOptions() {
      return this.uniqueOptions("level");
    },
    statusOptions() {
      const codes = ["ABIERTA", "NOTIFICADA", "VISTA", "EN_ATENCION", "CERRADA"];
      return [
        { text: this.$t("Reports.AlertTracking.toolbar.allOption"), value: "__ALL__" },
      ].concat(codes.map((c) => ({ text: this.statusLabel(c), value: c })));
    },
    activeFilterChips() {
      const chips = [];
      const config = [
        { key: "machine", empty: "__ALL__", label: this.$t("Reports.AlertTracking.chips.maquina"), icon: "mdi-factory" },
        { key: "sensor", empty: "__ALL__", label: this.$t("Reports.AlertTracking.chips.sensor"), icon: "mdi-access-point" },
        { key: "variable", empty: "__ALL__", label: this.$t("Reports.AlertTracking.chips.variable"), icon: "mdi-chart-bell-curve" },
        { key: "level", empty: "__ALL__", label: this.$t("Reports.AlertTracking.chips.nivel"), icon: "mdi-alert-octagon-outline" },
        { key: "status", empty: "__ALL__", label: this.$t("Reports.AlertTracking.chips.estado"), icon: "mdi-list-status" },
        { key: "startDate", empty: null, label: this.$t("Reports.AlertTracking.chips.desde"), icon: "mdi-calendar-start" },
        { key: "endDate", empty: null, label: this.$t("Reports.AlertTracking.chips.hasta"), icon: "mdi-calendar-end" },
      ];
      config.forEach((item) => {
        const value = this.filters[item.key];
        if (value && value !== item.empty) {
          chips.push({
            key: item.key,
            label: item.label,
            value: this.filterChipValue(item.key, value),
            icon: item.icon,
          });
        }
      });
      if (this.search.trim()) {
        chips.unshift({
          key: "search",
          label: this.$t("Reports.AlertTracking.chips.texto"),
          value: this.search.trim(),
          icon: "mdi-magnify",
        });
      }
      return chips;
    },
    activeFiltersCount() {
      return this.activeFilterChips.length;
    },
    summaryCards() {
      // Solo manejamos dos niveles: Alarma (critico/rojo) y Advertencia (control/naranja).
      const alarmaNombre = (this.alerts.find((x) => x.levelTone === "critical") || {}).level;
      const advertenciaNombre = (this.alerts.find((x) => x.levelTone === "control") || {}).level;
      return [
        {
          key: "alarma",
          label: this.$t("Reports.AlertTracking.summary.alarmas"),
          value: this.alerts.filter((x) => x.levelTone === "critical" && x.status !== "CERRADA").length,
          link: this.$t("Reports.AlertTracking.summary.verAlarmas"),
          icon: "mdi-alert-octagon-outline",
          tone: "critical",
          filter: { level: alarmaNombre },
        },
        {
          key: "advertencia",
          label: this.$t("Reports.AlertTracking.summary.advertencias"),
          value: this.alerts.filter((x) => x.levelTone === "control" && x.status !== "CERRADA").length,
          link: this.$t("Reports.AlertTracking.summary.verAdvertencias"),
          icon: "mdi-alert-outline",
          tone: "control",
          filter: { level: advertenciaNombre },
        },
        {
          key: "attention",
          label: this.$t("Reports.AlertTracking.summary.enAtencion"),
          value: this.alerts.filter((x) => x.status === "EN_ATENCION").length,
          link: this.$t("Reports.AlertTracking.summary.verEnAtencion"),
          icon: "mdi-wrench-outline",
          tone: "blue",
          filter: { status: "EN_ATENCION" },
        },
        {
          key: "open",
          label: this.$t("Reports.AlertTracking.summary.abiertas"),
          value: this.alerts.filter((x) => x.status === "ABIERTA").length,
          link: this.$t("Reports.AlertTracking.summary.verAbiertas"),
          icon: "mdi-bell-ring-outline",
          tone: "amber",
          filter: { status: "ABIERTA" },
        },
        {
          key: "vistas",
          label: this.$t("Reports.AlertTracking.summary.vistas"),
          value: this.alerts.filter((x) => x.status === "VISTA").length,
          link: this.$t("Reports.AlertTracking.summary.verVistas"),
          icon: "mdi-eye-check-outline",
          tone: "blue",
          filter: { status: "VISTA" },
        },
        {
          key: "closed",
          label: this.$t("Reports.AlertTracking.summary.cerradas"),
          value: this.alerts.filter((x) => x.status === "CERRADA").length,
          link: this.$t("Reports.AlertTracking.summary.verHistorial"),
          icon: "mdi-shield-check-outline",
          tone: "green",
          filter: { status: "CERRADA" },
        },
      ];
    },
    filteredAlerts() {
      const needle = this.search.toLowerCase().trim();
      return this.alerts.filter((alert) => {
        const matchesSearch = !needle || [
          alert.code,
          alert.message,
          alert.machine,
          alert.sensor,
          alert.variable,
          alert.value,
          alert.startDate,
          alert.lastDate,
        ].join(" ").toLowerCase().indexOf(needle) >= 0;
        return matchesSearch
          && this.matchesFilter(alert.machine, this.filters.machine, "__ALL__")
          && this.matchesFilter(alert.sensor, this.filters.sensor, "__ALL__")
          && this.matchesFilter(alert.variable, this.filters.variable, "__ALL__")
          && this.matchesFilter(alert.level, this.filters.level, "__ALL__")
          && this.matchesFilter(alert.status, this.filters.status, "__ALL__")
          && this.matchesDateRange(alert.startDateValue);
      });
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredAlerts.length / this.itemsPerPage));
    },
    paginatedAlerts() {
      const start = (this.page - 1) * this.itemsPerPage;
      return this.filteredAlerts.slice(start, start + this.itemsPerPage);
    },
    paginationStart() {
      if (this.filteredAlerts.length === 0) return 0;
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    paginationEnd() {
      return Math.min(this.page * this.itemsPerPage, this.filteredAlerts.length);
    },
    canConfirmStatus() {
      return this.selectedAlert && this.newStatus && this.comment.trim().length > 0;
    },
    availableStateActions() {
      if (!this.selectedAlert) return this.stateActions;
      return this.stateActions.filter((s) =>
        this.canTransition(this.selectedAlert.status, s.value)
      );
    },
  },
  mounted() {
    const savedLocale = localStorage.getItem('locale');
    if (savedLocale) this.$i18n.locale = savedLocale;
    this.getAlertas();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  watch: {
    active(value) {
      if (value) this.getAlertas();
    },
    filters: {
      deep: true,
      handler() {
        this.page = 1;
      },
    },
    search() {
      this.page = 1;
    },
    itemsPerPage() {
      this.page = 1;
    },
    pageCount(value) {
      if (this.page > value) this.page = value;
    },
  },
  methods: {
    setLocale(locale) {
      if (!locale) return;
      this.$i18n.locale = locale;
      localStorage.setItem('locale', locale);
    },
    defaultFilters() {
      return {
        machine: "__ALL__",
        sensor: "__ALL__",
        variable: "__ALL__",
        level: "__ALL__",
        status: "__ALL__",
        startDate: null,
        endDate: null,
      };
    },
    uniqueOptions(key) {
      const values = this.alerts.map((item) => item[key]).filter(Boolean);
      return [
        { text: this.$t("Reports.AlertTracking.toolbar.allOption"), value: "__ALL__" },
      ].concat(Array.from(new Set(values)).map((v) => ({ text: v, value: v })));
    },
    matchesFilter(value, filter, emptyLabel) {
      return filter === emptyLabel || value === filter;
    },
    filterChipValue(key, value) {
      if (key === "status") return this.statusLabel(value);
      if (key === "startDate" || key === "endDate") return this.fechaCorta(value);
      return value;
    },
    matchesDateRange(value) {
      const date = this.toDateKey(value);
      const start = this.toDateKey(this.filters.startDate);
      const end = this.toDateKey(this.filters.endDate);
      if (!date) return !start && !end;
      return (!start || date >= start) && (!end || date <= end);
    },
    quickFilter(filter) {
      this.search = "";
      this.filters = this.defaultFilters();
      if (filter.level) this.filters.level = filter.level;
      if (filter.status) this.filters.status = filter.status;
    },
    resetFilters() {
      this.search = "";
      this.filters = this.defaultFilters();
      this.page = 1;
    },
    removeFilter(key) {
      if (key === "search") {
        this.search = "";
        return;
      }
      const defaults = this.defaultFilters();
      this.filters[key] = defaults[key];
    },
    statusTone(status) {
      const tones = {
        ABIERTA: "critical",
        NOTIFICADA: "critical",
        VISTA: "blue",
        RECONOCIDA: "amber",
        EN_ATENCION: "blue",
        CERRADA: "green",
      };
      return tones[status] || "blue";
    },
    levelIcon(tone) {
      const icons = {
        critical: "mdi-alert-octagon",
        control: "mdi-alert",
        tolerance: "mdi-alert-rhombus-outline",
        normal: "mdi-check-circle-outline",
      };
      return icons[tone] || "mdi-alert";
    },
    statusLabel(status) {
      const key = `Reports.AlertTracking.statuses.${status}`;
      const label = this.$t(key);
      return label === key ? status : label;
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.autoRefreshSeconds = 180;
      this.autoRefreshTimer = setInterval(() => {
        if (this.autoRefreshSeconds <= 1) {
          this.autoRefreshSeconds = 180;
          this.getAlertas();
        } else {
          this.autoRefreshSeconds -= 1;
        }
      }, 1000);
    },
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
    },
    refreshNow() {
      this.autoRefreshSeconds = 180;
      this.getAlertas();
    },
    getAlertas() {
      this.loading = true;
      this.autoRefreshSeconds = 180;
      axios
        .get("/AlertaSeguimiento/Listar", { params: { paginar: false } })
        .then((response) => {
          const rows = (response.data && response.data.result) || [];
          this.alerts = rows.map((row) => this.mapAlerta(row));
        })
        .catch((error) => {
          console.log(error);
          this.showSnackbar(this.$t("Reports.AlertTracking.messages.couldNotLoad"));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    mapAlerta(row) {
      const inicio = this.splitFecha(row.fechaInicio);
      const ultima = this.splitFecha(row.fechaUltimaDeteccion);
      return {
        id: row.idAlerta,
        code: row.codigo,
        type: row.tipoAlerta,
        level: row.nivel,
        levelTone: row.nivelTono || "normal",
        machine: row.maquina,
        sensor: row.sensor,
        variable: row.variable,
        value: row.valorTexto,
        startDateValue: this.toDateKey(row.fechaInicio),
        startDate: inicio.date,
        startTime: inicio.time,
        lastDateValue: this.toDateKey(row.fechaUltimaDeteccion),
        lastDate: ultima.date,
        lastTime: ultima.time,
        status: row.estado,
        elapsed: row.tiempoTranscurrido,
        message: row.mensaje,
        detections: row.totalDetecciones,
        firstNotification: this.fechaCorta(row.fechaPrimeraNotificacion),
        lastNotification: this.fechaCorta(row.fechaUltimaNotificacion),
        history: [],
      };
    },
    splitFecha(fecha) {
      if (!fecha) return { date: "N/A", time: "" };
      const parts = String(fecha).split(" ");
      const ymd = (parts[0] || "").split("-");
      const date = ymd.length === 3 ? `${ymd[2]}/${ymd[1]}/${ymd[0]}` : parts[0];
      const time = parts[1] || "";
      return { date, time };
    },
    fechaCorta(fecha) {
      if (!fecha) return null;
      const parts = String(fecha).split(" ");
      const ymd = (parts[0] || "").split("-");
      const date = ymd.length === 3 ? `${ymd[2]}/${ymd[1]}/${ymd[0]}` : parts[0];
      const hm = (parts[1] || "").slice(0, 5);
      return `${date} ${hm}`.trim();
    },
    toDateKey(value) {
      if (!value) return null;
      const text = String(value).trim();
      const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) return isoMatch[0];
      const displayMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (displayMatch) return `${displayMatch[3]}-${displayMatch[2]}-${displayMatch[1]}`;
      return null;
    },
    openDetails(alert) {
      this.selectedAlert = alert;
      this.detailsDialog = true;
      axios
        .get(`/AlertaSeguimiento/Detalle/${alert.id}`)
        .then((response) => {
          const d = response.data || {};
          alert.status = d.estado || alert.status;
          this.selectedAlert = Object.assign({}, alert, {
            type: d.tipoAlerta || alert.type,
            level: d.nivel || alert.level,
            levelTone: d.nivelTono || alert.levelTone,
            machine: d.maquina || alert.machine,
            sensor: d.sensor || alert.sensor,
            variable: d.variable || alert.variable,
            tipoFalla: d.tipoFallaMaquina,
            status: d.estado || alert.status,
            message: d.mensaje || alert.message,
            value: d.valorTexto || alert.value,
            fechaInicioFull: d.fechaInicio,
            ultimaDeteccionFull: d.fechaUltimaDeteccion,
            tiempo: d.tiempoTranscurrido,
            detections: d.totalDetecciones != null ? d.totalDetecciones : alert.detections,
            firstNotification: this.fechaCorta(d.fechaPrimeraNotificacion),
            lastNotification: this.fechaCorta(d.fechaUltimaNotificacion),
            history: (d.historial || []).map((h) => ({
              action: h.accion,
              usuario: h.usuario,
              comment: h.comentario,
              date: h.fecha,
            })),
            detecciones: (d.detecciones || []).map((x) => ({
              fecha: x.fecha,
              valor: x.valorDetectado,
              nivel: x.nivel,
            })),
            notificaciones: (d.notificaciones || []).map((x) => ({
              medio: x.medio,
              estadoEnvio: x.estadoEnvio,
              fechaEnvio: x.fechaEnvio,
              intentos: x.intentos,
              mensajeError: x.mensajeError,
            })),
          });
        })
        .catch((error) => {
          console.log(error);
          this.showSnackbar(this.$t("Reports.AlertTracking.messages.couldNotLoadDetail"));
        });
    },
    openChangeStatus(alert, status) {
      this.selectedAlert = alert;
      const valid = this.stateActions
        .map((s) => s.value)
        .filter((v) => this.canTransition(alert.status, v));
      if (valid.length === 0) {
        this.showSnackbar(this.$t("Reports.AlertTracking.messages.alreadyClosed"));
        return;
      }
      const requested = status && valid.indexOf(status) >= 0 ? status : null;
      const sugerido = this.nextStatus(alert.status);
      this.newStatus = requested || (valid.indexOf(sugerido) >= 0 ? sugerido : valid[0]);
      this.comment = "";
      this.statusDialog = true;
    },
    canTransition(current, target) {
      const rules = {
        EN_ATENCION: ["ABIERTA", "NOTIFICADA", "VISTA", "RECONOCIDA"],
        CERRADA: ["ABIERTA", "NOTIFICADA", "VISTA", "RECONOCIDA", "EN_ATENCION"],
      };
      return (rules[target] || []).indexOf(current) >= 0;
    },
    nextStatus(status) {
      if (status === "ABIERTA" || status === "NOTIFICADA" || status === "VISTA" || status === "RECONOCIDA") return "EN_ATENCION";
      return "CERRADA";
    },
    confirmStatus() {
      if (!this.canConfirmStatus || this.saving) return;
      if (this.newStatus === "CERRADA") {
        this.closeConfirmDialog = true;
        return;
      }
      this.saveStatusChange();
    },
    confirmCloseStatus() {
      this.closeConfirmDialog = false;
      this.saveStatusChange();
    },
    saveStatusChange() {
      if (!this.canConfirmStatus || this.saving) return;
      const alert = this.selectedAlert;
      const endpoints = {
        RECONOCIDA: "Reconocer",
        EN_ATENCION: "PonerEnAtencion",
        CERRADA: "Cerrar",
      };
      const action = endpoints[this.newStatus];
      if (!action) return;
      this.saving = true;
      axios
        .put(`/AlertaSeguimiento/${action}/${alert.id}`, { comentario: this.comment })
        .then(() => {
          this.statusDialog = false;
          this.showSnackbar(this.$t("Reports.AlertTracking.messages.statusUpdated"));
          this.getAlertas();
        })
        .catch((error) => {
          console.log(error);
          this.showSnackbar(this.$t("Reports.AlertTracking.messages.couldNotUpdateStatus"));
        })
        .finally(() => {
          this.saving = false;
        });
    },
    addComment(alert) {
      const comentario = window.prompt(this.$t("Reports.AlertTracking.messages.commentPrompt", { code: alert.code }));
      if (!comentario || !comentario.trim()) return;
      axios
        .post(`/AlertaSeguimiento/Comentario/${alert.id}`, { comentario: comentario.trim() })
        .then(() => {
          this.showSnackbar(this.$t("Reports.AlertTracking.messages.commentAdded"));
          this.getAlertas();
          if (this.detailsDialog && this.selectedAlert && this.selectedAlert.id === alert.id) {
            this.openDetails(alert);
          }
        })
        .catch((error) => {
          console.log(error);
          this.showSnackbar(this.$t("Reports.AlertTracking.messages.couldNotAddComment"));
        });
    },
    showSnackbar(text) {
      this.snackbar.text = text;
      this.snackbar.show = true;
    },
  },
};
</script>

<style scoped>
.alerts-followup {
  min-height: 100vh;
  padding: 22px 24px 32px;
  background:
    linear-gradient(135deg, rgba(15, 36, 64, 0.03), rgba(15, 91, 255, 0.04)),
    #f4f7fb;
  color: #16243a;
}

.alerts-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  padding: 18px 22px;
  margin-bottom: 20px;
  border: 1px solid #dce6f2;
  border-left: 5px solid #0f5bff;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 18px 42px rgba(31, 55, 82, 0.08);
}

.alerts-hero__eyebrow,
.alerts-live {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #60728b;
}

.alerts-hero__status-dot,
.alerts-live__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #16a34a;
  box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.12);
  animation: live-pulse 2s ease-in-out infinite;
}

.alerts-live__count {
  display: inline-flex;
  align-items: center;
  min-width: 44px;
  justify-content: center;
  padding: 2px 8px;
  margin-left: 2px;
  border-radius: 999px;
  background: #eef3fb;
  color: #0f5bff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
}

.alerts-live__btn {
  margin-left: 2px;
}
.alerts-live__btn >>> .v-icon { color: #0f5bff !important; }

@keyframes live-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.35); }
  50% { box-shadow: 0 0 0 5px rgba(22, 163, 74, 0); }
}

.alerts-hero__title {
  margin: 4px 0 3px;
  font-size: 28px;
  font-weight: 850;
  letter-spacing: 0;
  color: #10233f;
}

.alerts-hero__subtitle {
  max-width: 620px;
  margin: 0;
  color: #60728b;
  font-size: 13px;
}

.alerts-hero__tools {
  display: flex;
  align-items: center;
  gap: 14px;
}

.alerts-hero__plant {
  width: 230px;
  background: #fff;
}

.alerts-icon-btn,
.alerts-action-btn {
  border: 1px solid #dce6f2;
  background: #ffffff;
}

.alerts-hero__avatar {
  color: #fff;
  font-weight: 800;
}

.alerts-kpis {
  display: grid;
  grid-template-columns: repeat(5, minmax(150px, 1fr));
  gap: 14px;
  margin-bottom: 14px;
}

.alerts-kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 104px;
  padding: 18px;
  border: 1px solid #dce6f2;
  border-radius: 8px;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.alerts-kpi:hover {
  transform: translateY(-2px);
  border-color: var(--tone);
  box-shadow: 0 16px 34px rgba(31, 55, 82, 0.11);
}

.alerts-kpi--critical { --tone: #ef233c; }
.alerts-kpi--control { --tone: #ff6b00; }
.alerts-kpi--amber { --tone: #f59e0b; }
.alerts-kpi--blue { --tone: #0f5bff; }
.alerts-kpi--green { --tone: #16a34a; }

.alerts-kpi__icon {
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: color-mix(in srgb, var(--tone) 11%, #fff);
}

.alerts-kpi__icon >>> .v-icon {
  color: var(--tone);
  font-size: 26px;
}

.alerts-kpi__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.alerts-kpi__label {
  font-size: 12px;
  font-weight: 800;
  color: #334a68;
}

.alerts-kpi__value {
  margin: 3px 0;
  font-size: 26px;
  line-height: 1;
  color: #10233f;
}

.alerts-kpi__link {
  font-size: 11px;
  font-weight: 800;
  color: #0f5bff;
}

.alerts-panel {
  border: 1px solid #dce6f2;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 18px 42px rgba(31, 55, 82, 0.08);
  overflow: hidden;
}

.alerts-toolbar,
.alerts-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
}

.alerts-reset-btn {
  min-width: 104px;
  height: 40px !important;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.alerts-toolbar {
  justify-content: flex-start;
  border-bottom: 1px solid #e8eef6;
  background:
    radial-gradient(circle at top left, rgba(15, 91, 255, 0.08), transparent 28%),
    #fbfcfe;
}

.alerts-search {
  max-width: 320px;
  min-width: 260px;
}

.alerts-filter-chips {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  gap: 8px;
  min-width: 180px;
  overflow-x: auto;
  padding: 2px 0;
}

.alerts-filter-chip {
  flex: 0 0 auto;
  border: 1px solid rgba(15, 91, 255, 0.16) !important;
  background: #eef5ff !important;
  color: #0f3f88 !important;
  font-weight: 800;
}

.alerts-filter-empty {
  color: #7a8da7;
  font-size: 12px;
  font-weight: 750;
  white-space: nowrap;
}

.alerts-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.alerts-filter-btn {
  position: relative;
  min-width: 110px;
  height: 40px !important;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: none;
  box-shadow: 0 10px 22px rgba(15, 91, 255, 0.18) !important;
}

.alerts-filter-btn__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 999px;
  background: #ffffff;
  color: #0f5bff;
  font-size: 11px;
  font-weight: 900;
}

.filters-dialog {
  position: relative;
  padding: 26px;
  border: 1px solid #e4ebf4;
  border-radius: 12px;
  background: #ffffff;
  color: #16243a;
  box-shadow: 0 24px 64px rgba(15, 36, 64, 0.18);
}

.filters-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: #f5f7fb;
  transition: background 0.18s ease, transform 0.18s ease;
}

.filters-dialog__close:hover {
  background: #eaf0f8;
  transform: translateY(-1px);
}

.filters-dialog__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 42px 12px 0;
  margin-bottom: 10px;
}

.filters-dialog__header span {
  color: #63758f;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.filters-dialog__header h2 {
  margin: 2px 0 0;
  color: #10233f;
  font-size: 23px;
  font-weight: 900;
  letter-spacing: 0;
}

.filters-dialog__icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #eef5ff;
  box-shadow: inset 0 0 0 1px rgba(15, 91, 255, 0.12);
}

.filters-dialog__icon >>> .v-icon {
  color: #0f5bff;
}

.filters-dialog__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 18px;
  border: 1px solid #e8eef6;
  border-radius: 12px;
  background: #fbfcfe;
}

.filters-dialog__grid >>> .v-input__slot {
  background: #ffffff !important;
  border-radius: 8px !important;
}

.filters-dialog__grid >>> .v-text-field--outlined fieldset {
  border-color: #dbe5f1;
}

.filters-dialog__grid >>> .v-input--is-focused fieldset {
  border-color: #0f5bff !important;
}

.filters-dialog__grid >>> .v-icon {
  color: #6f8198;
}

.filters-dialog__wide {
  grid-column: span 3;
}

.filters-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.alerts-table-wrap {
  overflow-x: auto;
}

.alerts-table {
  width: 100%;
  min-width: 1080px;
  border-collapse: collapse;
}

.alerts-table th {
  padding: 15px 14px;
  background: #f8fafc;
  color: #334a68;
  font-size: 11px;
  font-weight: 850;
  text-align: left;
  white-space: nowrap;
  border-bottom: 1px solid #e8eef6;
}

.alerts-table td {
  padding: 15px 14px;
  color: #223653;
  font-size: 12px;
  font-weight: 650;
  border-bottom: 1px solid #e8eef6;
  vertical-align: middle;
}

.alerts-table tr:hover td {
  background: #f8fbff;
}

.priority-pill,
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 92px;
  justify-content: center;
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 850;
}

.priority-pill {
  color: #fff;
}

.priority-pill--critical { background: #ef233c; }
.priority-pill--control { background: #ff6b00; }
.priority-pill--tolerance { background: #f6bd3a; }
.priority-pill--normal { background: #16a34a; }

.priority-pill >>> .v-icon {
  color: #fff;
}

.alert-id,
.machine-name {
  color: #14294a;
  font-weight: 850;
}

.alert-message,
.machine-meta,
.time-muted {
  margin-top: 3px;
  color: #60728b;
  font-size: 11px;
  font-weight: 600;
}

.alert-value {
  font-weight: 900;
}

.alert-value--critical { color: #ef233c; }
.alert-value--control { color: #ff6b00; }
.alert-value--tolerance { color: #d99a00; }
.alert-value--normal { color: #16a34a; }

.status-pill--critical {
  color: #dc2626;
  background: #fee2e2;
}

.status-pill--amber {
  color: #b45309;
  background: #fef3c7;
}

.status-pill--blue {
  color: #0f5bff;
  background: #dbeafe;
}

.status-pill--green {
  color: #15803d;
  background: #dcfce7;
}

.alerts-actions {
  display: flex;
  align-items: center;
  gap: 7px;
}

.alerts-menu .v-list-item__title {
  color: #203653;
  font-size: 12px;
  font-weight: 700;
}

.alerts-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 160px;
  color: #60728b;
  font-weight: 700;
}

.alerts-footer {
  justify-content: space-between;
  color: #60728b;
  font-size: 12px;
  font-weight: 700;
  border-top: 1px solid #e8eef6;
}

.alerts-page-size {
  max-width: 150px;
}

.details-dialog-shell {
  border-radius: 18px !important;
  overflow: hidden !important;
  box-shadow: 0 32px 80px rgba(15, 36, 64, 0.28) !important;
}

.details-dialog {
  position: relative;
  border-radius: 18px;
  background: #ffffff;
  color: #16243a;
  overflow: hidden;
}

/* ---- Hero ---- */
.details-dialog__hero {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 24px 20px;
  background:
    radial-gradient(circle at top left, color-mix(in srgb, var(--hero-accent, #0f5bff) 10%, transparent), transparent 46%),
    #ffffff;
  border-bottom: 1px solid #eef2f7;
  color: #16243a;
}

.details-dialog__hero::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: var(--hero-accent, #0f5bff);
}

.details-dialog__hero--critical { --hero-accent: #ef233c; }
.details-dialog__hero--control { --hero-accent: #ff6b00; }
.details-dialog__hero--tolerance { --hero-accent: #f6bd3a; }
.details-dialog__hero--normal { --hero-accent: #16a34a; }

.details-dialog__hero-icon {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--hero-accent, #0f5bff) 12%, #ffffff);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--hero-accent, #0f5bff) 22%, transparent);
  flex-shrink: 0;
}

.details-dialog__hero-icon >>> .v-icon {
  color: var(--hero-accent, #0f5bff);
  font-size: 26px;
}

.details-dialog__hero-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.details-dialog__eyebrow {
  display: block;
  color: #8094ab;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.details-dialog__code {
  margin: 3px 0 0;
  font-size: 23px;
  font-weight: 900;
  letter-spacing: 0;
  color: #10233f;
}

.details-dialog__msg {
  margin: 4px 0 0;
  color: #60728b;
  font-size: 12.5px;
  font-weight: 600;
}

.details-dialog__badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.details-dialog__type-chip {
  padding: 5px 11px;
  border-radius: 999px;
  background: #eef2f7;
  color: #51637c;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
}

.details-dialog__close {
  color: #51637c !important;
  background: #f1f5f9 !important;
  flex-shrink: 0;
}

.details-dialog__close:hover {
  background: #e6edf5 !important;
}

/* ---- Body ---- */
.details-dialog__body {
  max-height: 64vh;
  overflow-y: auto;
  padding: 22px 24px 26px;
}

.details-dialog__value {
  font-size: 15px;
}

.change-dialog {
  position: relative;
  padding: 26px;
  border: 1px solid #e4ebf4;
  border-radius: 12px;
  background: #ffffff;
  color: #16243a;
  box-shadow: 0 24px 64px rgba(15, 36, 64, 0.18);
}

.change-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: #f5f7fb;
  transition: background 0.18s ease, transform 0.18s ease;
}

.change-dialog__close:hover {
  background: #eaf0f8;
  transform: translateY(-1px);
}

.details-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8eef6;
}

.change-dialog__header {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-start;
  padding: 0 42px 16px 0;
  border-bottom: 1px solid #e8eef6;
}

.change-dialog__header h2,
.details-dialog__header h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 900;
  color: #10233f;
  letter-spacing: 0;
}

.change-dialog__header p {
  margin: 5px 0 0;
  color: #60728b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

.change-dialog__icon {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(15, 36, 64, 0.06);
}

.change-dialog__icon--critical { background: #fff1f2; }
.change-dialog__icon--critical >>> .v-icon { color: #e11d48; }
.change-dialog__icon--control { background: #fff7ed; }
.change-dialog__icon--control >>> .v-icon { color: #ea580c; }
.change-dialog__icon--tolerance { background: #fffbeb; }
.change-dialog__icon--tolerance >>> .v-icon { color: #d97706; }
.change-dialog__icon--normal { background: #ecfdf5; }
.change-dialog__icon--normal >>> .v-icon { color: #059669; }

.change-dialog__summary,
.details-dialog__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 18px 0;
}

.details-dialog__grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 4px 0 6px;
}

.change-dialog__summary > div,
.details-dialog__grid > div {
  min-width: 0;
  padding: 13px 14px;
  border: 1px solid #e8eef6;
  border-radius: 10px;
  background: #fbfcfe;
}

.details-dialog__grid > div {
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.details-dialog__grid > div:hover {
  border-color: #cfdcec;
  box-shadow: 0 8px 20px rgba(31, 55, 82, 0.07);
  transform: translateY(-1px);
}

.change-dialog__summary span,
.details-dialog__grid small {
  display: block;
  color: #60728b;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.change-dialog__summary strong,
.details-dialog__grid strong {
  display: block;
  margin-top: 5px;
  color: #10233f;
  overflow-wrap: anywhere;
}

.change-dialog__summary small {
  display: block;
  margin-top: 3px;
  color: #60728b;
}

.change-dialog__section-title {
  margin: 4px 0 10px;
  color: #334a68;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.state-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.state-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 58px;
  padding: 12px;
  border: 1px solid #dfe7f2;
  border-radius: 10px;
  background: #ffffff;
  color: #334a68;
  font-weight: 850;
  transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease, transform 0.18s ease;
}

.state-card:hover {
  transform: translateY(-1px);
  border-color: var(--state);
  background: var(--state-soft);
  color: var(--state);
}

.state-card--active {
  border-color: var(--state);
  background: var(--state-soft);
  color: var(--state);
  box-shadow: inset 0 0 0 1px var(--state-ring);
}

.state-card >>> .v-icon {
  color: currentColor;
}

.state-card--amber {
  --state: #b45309;
  --state-soft: #fff7ed;
  --state-ring: rgba(180, 83, 9, 0.12);
}

.state-card--blue {
  --state: #0f5bff;
  --state-soft: #eef5ff;
  --state-ring: rgba(15, 91, 255, 0.12);
}

.state-card--green {
  --state: #15803d;
  --state-soft: #ecfdf5;
  --state-ring: rgba(21, 128, 61, 0.12);
}

.change-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}

.change-dialog__cancel {
  min-width: 104px;
  height: 40px !important;
  border-color: #dce6f2 !important;
  border-radius: 8px;
  color: #334a68 !important;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.change-dialog__confirm {
  min-width: 112px;
  height: 40px !important;
  border-radius: 8px;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: none;
  box-shadow: 0 10px 22px rgba(15, 91, 255, 0.18) !important;
}

.change-dialog__confirm.v-btn--disabled {
  background: #eef2f7 !important;
  color: #71839c !important;
  box-shadow: none !important;
}

.change-dialog__note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  padding: 11px 12px;
  border: 1px solid #dce8fb;
  border-radius: 8px;
  background: #f7fbff;
  color: #47617f;
  font-size: 12px;
  font-weight: 700;
}

.change-dialog__note >>> .v-icon {
  color: #0f5bff;
}

.close-confirm-dialog {
  position: relative;
  padding: 24px;
  border: 1px solid #f1d5d5;
  border-radius: 12px;
  background: #ffffff;
  color: #16243a;
  box-shadow: 0 24px 64px rgba(15, 36, 64, 0.18);
}

.close-confirm-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: #f8f3f3;
  transition: background 0.18s ease, transform 0.18s ease;
}

.close-confirm-dialog__close:hover {
  background: #fee2e2;
  transform: translateY(-1px);
}

.close-confirm-dialog__header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-right: 42px;
}

.close-confirm-dialog__icon {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #fff1f2;
  box-shadow: inset 0 0 0 1px rgba(225, 29, 72, 0.12);
}

.close-confirm-dialog__icon >>> .v-icon {
  color: #e11d48;
}

.close-confirm-dialog__header span {
  color: #be123c;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.close-confirm-dialog__header h2 {
  margin: 2px 0 0;
  color: #10233f;
  font-size: 23px;
  font-weight: 900;
  letter-spacing: 0;
}

.close-confirm-dialog__header p {
  margin: 5px 0 0;
  color: #60728b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

.close-confirm-dialog__body {
  margin: 18px 0;
  padding: 14px;
  border: 1px solid #f3d6dc;
  border-radius: 10px;
  background: #fff8f9;
  color: #6b3140;
  font-size: 13px;
  font-weight: 750;
  line-height: 1.45;
}

.close-confirm-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.details-dialog__header span {
  color: #60728b;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.details-dialog__timeline h3 {
  margin: 0 0 12px;
  color: #10233f;
  font-size: 15px;
}

/* ---- Section headers ---- */
.details-dialog__section-label {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 2px 0 10px;
  color: #334a68;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.details-dialog__section-label >>> .v-icon {
  color: #0f5bff;
}

.details-dialog__section {
  margin-top: 22px;
}

.details-dialog__section h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  color: #10233f;
  font-size: 14px;
  font-weight: 850;
}

.details-dialog__section h3 >>> .v-icon {
  color: #0f5bff;
}

/* ---- Tables ---- */
.details-table-wrap {
  border: 1px solid #e8eef6;
  border-radius: 12px;
  overflow: hidden;
}

.details-table {
  width: 100%;
  border-collapse: collapse;
}

.details-table th {
  padding: 10px 12px;
  background: #f7f9fc;
  color: #51637c;
  font-size: 10px;
  font-weight: 850;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid #e8eef6;
}

.details-table td {
  padding: 10px 12px;
  color: #223653;
  font-size: 12px;
  font-weight: 600;
  border-bottom: 1px solid #eef3f8;
}

.details-table tbody tr:last-child td {
  border-bottom: 0;
}

.details-table tbody tr:hover td {
  background: #f8fbff;
}

.details-table__num {
  font-variant-numeric: tabular-nums;
  font-weight: 850;
  color: #14294a;
}

/* ---- Envio pill ---- */
.envio-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background: #eef2f7;
  color: #5d6f87;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.04em;
}

.envio-pill--enviado { background: #dcfce7; color: #15803d; }
.envio-pill--pendiente { background: #fef3c7; color: #b45309; }
.envio-pill--error { background: #fee2e2; color: #dc2626; }

/* ---- Timeline ---- */
.details-dialog__timeline {
  position: relative;
  margin-top: 2px;
}

.timeline-item {
  position: relative;
  padding: 0 0 16px 22px;
}

.timeline-item::before {
  content: "";
  position: absolute;
  left: 5px;
  top: 4px;
  bottom: -2px;
  width: 2px;
  background: #e8eef6;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-item:last-child::before {
  display: none;
}

.timeline-item__dot {
  position: absolute;
  left: 0;
  top: 3px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #0f5bff;
  box-shadow: 0 0 0 4px #ffffff, 0 0 0 5px #d8e6ff;
}

.timeline-item__dot--critical { background: #ef233c; box-shadow: 0 0 0 4px #fff, 0 0 0 5px #fde0e4; }
.timeline-item__dot--amber { background: #f59e0b; box-shadow: 0 0 0 4px #fff, 0 0 0 5px #fdecc8; }
.timeline-item__dot--blue { background: #0f5bff; box-shadow: 0 0 0 4px #fff, 0 0 0 5px #d8e6ff; }
.timeline-item__dot--green { background: #16a34a; box-shadow: 0 0 0 4px #fff, 0 0 0 5px #cdeed8; }

.timeline-item__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}

.timeline-item__head strong {
  color: #10233f;
  font-size: 13px;
  font-weight: 850;
}

.timeline-item__head small {
  color: #8ba0b8;
  font-size: 11px;
  white-space: nowrap;
}

.timeline-item__user {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 3px;
  color: #0f5bff;
  font-size: 11px;
  font-weight: 800;
}

.timeline-item__user >>> .v-icon {
  color: #0f5bff;
}

.timeline-item__body p {
  margin: 4px 0 0;
  color: #5d6f87;
  font-size: 12px;
}

/* ---- Empty states ---- */
.details-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border: 1px dashed #d7e0ea;
  border-radius: 12px;
  background: #fafcfe;
  color: #8093a8;
  font-size: 12px;
  font-weight: 700;
}

.details-empty >>> .v-icon {
  color: #aab8c8;
}

@media (max-width: 1100px) {
  .alerts-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .alerts-hero {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (max-width: 720px) {
  .alerts-followup {
    padding: 14px;
  }

  .alerts-kpis,
  .change-dialog__summary,
  .details-dialog__grid,
  .state-grid {
    grid-template-columns: 1fr;
  }

  .alerts-hero__tools,
  .alerts-toolbar,
  .alerts-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .alerts-hero__plant,
  .alerts-search,
  .alerts-filter-chips,
  .alerts-toolbar__actions,
  .alerts-reset-btn,
  .alerts-filter-btn {
    width: 100%;
    max-width: none;
  }

  .alerts-search {
    min-width: 0;
  }

  .alerts-filter-chips {
    min-width: 0;
  }

  .alerts-toolbar__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .filters-dialog {
    padding: 20px;
  }

  .filters-dialog__grid {
    grid-template-columns: 1fr;
    padding: 14px;
  }

  .filters-dialog__wide {
    grid-column: auto;
  }

  .change-dialog {
    padding: 20px;
  }

  .change-dialog__header {
    align-items: flex-start;
  }

  .change-dialog__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .change-dialog__cancel,
  .change-dialog__confirm {
    width: 100%;
  }

  .close-confirm-dialog {
    padding: 20px;
  }

  .close-confirm-dialog__actions {
    display: grid;
    grid-template-columns: 1fr;
  }
}

/* Alarm follow-up skin aligned to SensoresTiempoReal step cards */
.monitor-header-card {
  overflow: hidden;
  border-radius: 18px !important;
  border-color: #dbe7f3 !important;
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06) !important;
}

.monitor-header {
  position: relative;
  background:
    radial-gradient(circle at top left, rgba(239, 35, 60, 0.16), transparent 32%),
    linear-gradient(135deg, #ffffff 0%, #fffafa 55%, #fff1f3 100%);
}

.monitor-header::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(115deg, transparent 22%, rgba(255, 255, 255, 0.6) 44%, transparent 63%);
  transform: translateX(-135%);
  animation: header-sheen 1.35s ease .2s both;
  pointer-events: none;
}

.monitor-header__avatar {
  box-shadow: 0 10px 22px rgba(239, 35, 60, 0.24);
}

.monitor-header__kicker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  color: #334155;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.monitor-header__kicker-line {
  width: 34px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, #0f172a 0%, #ef233c 100%);
}

.monitor-header__title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 2px;
  color: #0f172a;
  text-shadow: 0 10px 26px rgba(239, 35, 60, 0.12);
}

.monitor-header__title-main {
  color: #0f172a;
  font-size: clamp(1.8rem, 3.2vw, 2.45rem);
  font-weight: 900;
  letter-spacing: -.05em;
  line-height: .95;
}

.monitor-header__title-ghost {
  color: #ef233c;
  font-size: clamp(1rem, 1.6vw, 1.2rem);
  font-weight: 700;
  letter-spacing: .18em;
  text-transform: uppercase;
}

.monitor-header__title-accent {
  width: 84px;
  height: 4px;
  margin: 7px 0 8px;
  border-radius: 999px;
  background: linear-gradient(90deg, #ef233c 0%, #fb7185 55%, rgba(251, 113, 133, 0) 100%);
}

.alerts-summary-row {
  flex-wrap: nowrap;
  padding: 14px 16px;
  border: 1px solid #d8e2ec;
  border-radius: 18px;
  background:
    linear-gradient(135deg, rgba(240, 253, 250, .72) 0%, rgba(255, 255, 255, .96) 28%, #ffffff 100%);
}

.alerts-summary-item {
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
}

.alerts-summary-col {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
}

.alerts-step-card {
  --step-color: #2878de;
  width: 100%;
  min-height: 118px;
  display: grid;
  grid-template-columns: 48px 1fr;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid #ccd9e8;
  border-radius: 18px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 18px 42px rgba(31, 55, 82, .07);
  cursor: pointer;
  text-align: center;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
}

.alerts-step-card:hover {
  transform: translateY(-3px);
  border-color: color-mix(in srgb, var(--step-color) 36%, #cfdcec);
  box-shadow: 0 22px 48px rgba(31, 55, 82, .13);
}

.alerts-step-card--critical { --step-color: #ef4e63; }
.alerts-step-card--control { --step-color: #f97316; }
.alerts-step-card--amber { --step-color: #f59e0b; }
.alerts-step-card--blue { --step-color: #2878de; }
.alerts-step-card--green { --step-color: #0f9f6e; }

.alerts-step-card__icon {
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 15px 30px rgba(31, 55, 82, .11);
}

.alerts-step-card__icon >>> .v-icon {
  color: var(--step-color) !important;
  font-size: 24px !important;
}

.alerts-step-card__label {
  color: #253247;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .14em;
  text-transform: uppercase;
}

.alerts-step-card__value {
  margin: 4px 0;
  color: var(--step-color);
  font-size: 36px;
  font-weight: 900;
  line-height: .95;
}

.alerts-step-card__cta {
  color: #657793;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.alerts-summary-chevron {
  flex: 0 0 auto;
}

.alerts-panel {
  border-color: #bdcbd9;
  border-radius: 12px;
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.05);
}

.alerts-filters {
  display: grid;
  grid-template-columns: repeat(5, minmax(120px, 1fr));
  gap: 10px;
  align-items: center;
  margin-bottom: 14px;
}

.alerts-export-btn,
.alerts-hero,
.alerts-kpis {
  display: none !important;
}

.alerts-action-btn {
  width: 34px !important;
  height: 34px !important;
  border: 1px solid #d4dee8 !important;
  border-radius: 8px !important;
  background: #fff !important;
  box-shadow: none !important;
}

.alerts-action-btn:hover {
  background: #f3f8fc !important;
  border-color: #9fc9df !important;
}

.alerts-actions >>> .v-icon,
.alerts-action-btn >>> .v-icon,
.alerts-menu >>> .v-icon,
.alerts-action-btn .v-icon,
.alerts-menu .v-icon {
  color: #203653 !important;
  caret-color: #203653 !important;
  opacity: 1 !important;
}

.alerts-action-btn.v-btn--disabled {
  opacity: .55 !important;
}

.alerts-action-btn.v-btn--disabled >>> .v-icon,
.alerts-action-btn.v-btn--disabled .v-icon {
  color: #7b8da6 !important;
  caret-color: #7b8da6 !important;
  opacity: 1 !important;
}

@keyframes header-sheen {
  to {
    transform: translateX(135%);
  }
}

/* ============================================================
   Rediseño: Tiles de señal + tabla de instrumentos (responsiva)
   ============================================================ */
.mono {
  font-family: "Roboto Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}

/* --- Tiles de resumen --- */
.signal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
}

.signal-tile {
  --sev: #2563eb;
  --sev-soft: #e7efff;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 116px;
  padding: 18px 20px 14px;
  border: 1px solid #e8edf4;
  border-radius: 18px;
  background: #ffffff;
  text-align: center;
  cursor: pointer;
  overflow: hidden;
  box-shadow: 0 10px 26px rgba(15, 30, 54, 0.05);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}
.signal-tile:hover {
  transform: translateY(-4px);
  border-color: color-mix(in srgb, var(--sev) 45%, #e8edf4);
  box-shadow: 0 22px 46px rgba(15, 30, 54, 0.12);
}
.signal-tile:focus-visible {
  outline: 2px solid var(--sev);
  outline-offset: 2px;
}

.signal-tile__main {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
}
.signal-tile__icon {
  position: relative;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 42%, var(--sev-soft) 0%, #ffffff 72%);
  box-shadow: 0 0 26px -6px var(--sev-soft);
  border: 1px solid color-mix(in srgb, var(--sev) 14%, transparent);
  transition: transform 0.22s ease;
}
.signal-tile:hover .signal-tile__icon { transform: scale(1.06); }
.signal-tile__icon >>> .v-icon {
  color: var(--sev) !important;
  font-size: 26px !important;
}
.signal-tile__info {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
}
.signal-tile__value {
  font-family: "Roboto Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-variant-numeric: tabular-nums;
  font-size: 32px;
  font-weight: 800;
  line-height: 1;
  color: #0f1e36;
}
.signal-tile__label {
  margin-top: 5px;
  font-size: 12.5px;
  font-weight: 600;
  color: #6b7c95;
}

.signal-tile__foot {
  display: grid;
  place-items: center;
  min-height: 16px;
}
.signal-tile__accent {
  grid-area: 1 / 1;
  width: 34px;
  height: 3px;
  border-radius: 999px;
  background: var(--sev);
  opacity: 0.85;
  transition: opacity 0.2s ease;
}
.signal-tile__cta {
  grid-area: 1 / 1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--sev);
  opacity: 0;
  transform: translateY(3px);
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.signal-tile__cta >>> .v-icon { color: var(--sev) !important; }
.signal-tile:hover .signal-tile__accent,
.signal-tile:focus-visible .signal-tile__accent {
  opacity: 0;
  transform: translateY(4px);
}
.signal-tile:hover .signal-tile__cta,
.signal-tile:focus-visible .signal-tile__cta {
  opacity: 1;
  transform: translateY(0);
}

.signal-tile--critical { --sev: #e5384d; --sev-soft: #fde8ea; }
.signal-tile--control  { --sev: #f97316; --sev-soft: #fff0e6; }
.signal-tile--amber    { --sev: #d99413; --sev-soft: #fff6da; }
.signal-tile--blue     { --sev: #2563eb; --sev-soft: #e7efff; }
.signal-tile--green    { --sev: #16a34a; --sev-soft: #e7f6ec; }

/* --- Tabla de instrumentos --- */
.signal-table {
  width: 100%;
  min-width: 940px;
  border-collapse: collapse;
}
.signal-table thead th {
  padding: 13px 16px;
  background: #f6f9fd;
  color: #5a6b85;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  text-align: left;
  white-space: nowrap;
  border-bottom: 1px solid #e6edf5;
}
.signal-table thead th.signal-table__ah { text-align: center; }
.signal-row {
  --sev: #cbd5e1;
  transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}
.signal-row--critical { --sev: #e5384d; }
.signal-row--control  { --sev: #f97316; }
.signal-row--tolerance { --sev: #d99413; }
.signal-row--normal   { --sev: #16a34a; }
.signal-table tbody td {
  padding: 14px 16px;
  font-size: 13px;
  color: #24364f;
  border-bottom: 1px solid #eef2f8;
  vertical-align: middle;
}
.signal-table tbody td:first-child {
  box-shadow: inset 4px 0 0 var(--sev);
}
.signal-row:hover td {
  background: #f7fbff;
}

.cell-strong {
  font-weight: 800;
  color: #0f1e36;
  font-size: 13.5px;
}
.cell-sub {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 3px;
  color: #6b7c95;
  font-size: 12px;
  font-weight: 600;
}
.cell-sub >>> .v-icon { color: #9aa9bf !important; }

.sev-pill {
  --c: #64748b;
  --cb: #eef2f7;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 104px;
  justify-content: center;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: var(--c);
  background: var(--cb);
  border: 1px solid color-mix(in srgb, var(--c) 22%, transparent);
}
.sev-pill >>> .v-icon { color: var(--c) !important; }
.sev-pill--critical { --c: #e5384d; --cb: #fde8ea; }
.sev-pill--control  { --c: #ea6a0c; --cb: #fff0e6; }
.sev-pill--tolerance { --c: #b9790a; --cb: #fff6da; }
.sev-pill--normal   { --c: #15803d; --cb: #e7f6ec; }

.readout {
  display: inline-block;
  padding: 5px 11px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 800;
  color: #0f1e36;
  background: #eef2f8;
}
.readout--critical { color: #e5384d; background: #fde8ea; }
.readout--control  { color: #ea6a0c; background: #fff0e6; }
.readout--tolerance { color: #b9790a; background: #fff6da; }
.readout--normal   { color: #15803d; background: #e7f6ec; }

.state-chip {
  --c: #2563eb;
  --cb: #e7efff;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 800;
  color: var(--c);
  background: var(--cb);
}
.state-chip__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--c);
}
.state-chip--critical { --c: #dc2626; --cb: #fde8ea; }
.state-chip--critical .state-chip__dot { animation: state-pulse 1.6s ease-in-out infinite; }
.state-chip--amber { --c: #b45309; --cb: #fef3c7; }
.state-chip--blue  { --c: #2563eb; --cb: #e7efff; }
.state-chip--green { --c: #15803d; --cb: #e7f6ec; }

.elapsed {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 700;
  color: #3a4d6b;
}
.elapsed >>> .v-icon { color: #93a2b8 !important; }

@keyframes state-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.45); }
  50% { box-shadow: 0 0 0 5px rgba(220, 38, 38, 0); }
}

/* --- Responsiva: la tabla colapsa a tarjetas --- */
@media (max-width: 960px) {
  .alerts-table-wrap { overflow-x: visible; }
  .signal-table { min-width: 0; }
  .signal-table thead { display: none; }
  .signal-table,
  .signal-table tbody,
  .signal-table tr,
  .signal-table td { display: block; width: 100%; }
  .signal-table tbody tr.signal-row {
    margin-bottom: 14px;
    padding: 6px 6px 10px;
    border: 1px solid #e6edf5;
    border-left: 4px solid var(--sev);
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 10px 26px rgba(15, 30, 54, 0.06);
  }
  .signal-table tbody td {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 9px 14px;
    border: none;
    text-align: right;
  }
  .signal-table tbody td:first-child { box-shadow: none; }
  .signal-table tbody td::before {
    content: attr(data-label);
    flex: 0 0 40%;
    text-align: left;
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #8194ad;
  }
  .signal-table tbody td .cell-sub { justify-content: flex-end; }
  .signal-table tbody td.signal-table__actions { justify-content: flex-end; }
  .signal-table tbody td.signal-table__actions::before { flex: 1 1 auto; }
}

@media (prefers-reduced-motion: reduce) {
  .signal-tile,
  .signal-tile__icon,
  .signal-tile__wave path,
  .signal-tile__accent,
  .signal-tile__cta,
  .signal-row,
  .signal-row td { transition: none !important; }
  .signal-tile:hover { transform: none; }
  .signal-tile:hover .signal-tile__icon { transform: none; }
  .state-chip--critical .state-chip__dot,
  .alerts-live__dot { animation: none; }
}

.str-language-control {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(25, 118, 210, 0.35);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(25, 118, 210, 0.14);
  display: inline-flex;
  padding: 6px 8px;
}

.str-language-toggle {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px rgba(25, 118, 210, 0.12);
}

.str-language-toggle ::v-deep .v-btn {
  border-radius: 0 !important;
  border: none !important;
  margin: 0 !important;
}

.str-language-btn {
  background: #eef4fd !important;
  color: #1565c0 !important;
  font-weight: 800;
  letter-spacing: .4px;
  min-width: 48px !important;
  transition: all .2s ease;
}

.str-language-btn--active {
  background: linear-gradient(135deg, #1e88e5 0%, #1565c0 100%) !important;
  color: #ffffff !important;
}
</style>

<style>
/* Tooltip de acciones (se renderiza fuera del componente, por eso no es scoped) */
.v-tooltip__content.alerts-tooltip {
  opacity: 1 !important;
  background: #0f2440 !important;
  color: #ffffff !important;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 6px 12px;
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(15, 36, 64, 0.32);
}
</style>
